import { useEffect, useMemo, useState , useRef} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
    CalendarDays,
    Car,
    CheckCircle2,
    CreditCard,
    Mail,
    MapPin,
    MessageSquare,
    Minus,
    Phone,
    Plus,
    ShieldCheck,
    Clock,
    UserRound,
    Users,
} from "lucide-react";
import {
    fetchTripDetails,
    getBookingJourney,
    saveBookingTravelers,
} from "../../../services/restapiService";
import PaymentLogin from "../PaymentPage/PaymentLogin";
import { encodeJourneyId } from "../../../utils/url";
import { motion } from "motion/react";

const emptyTraveler = { name: "", email: "", phone: "", message: "" };

const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (value) => {
    if (!value) return "Date pending";

    return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
};

function BookingSummary() {
    const { state } = useLocation();
    const navigate = useNavigate();
const travelerSectionRef = useRef(null);
    const journeyId = state?.journeyId || localStorage.getItem("journeyId");

    const [loading, setLoading] = useState(false);
    const [bookingData, setBookingData] = useState(null);
    const [participants, setParticipants] = useState({
        adult: 0,
        child: 0,
        infant: 0,
    });
    const [travelers, setTravelers] = useState([{ ...emptyTraveler }]);
    const [showLoginModal, setShowLoginModal] = useState(false);


    useEffect(() => {
        const loadJourney = async () => {
            try {
                setLoading(true);

                const res = await getBookingJourney(journeyId);

                if (res.data.success) {

                    const journey = res.data.data;
                    setBookingData(journey);
                    setParticipants({
                        adult: Number(journey?.adults || 0),
                        child: Number(journey?.children || 0),
                        infant: Number(journey?.infants || 0),
                    });

                    const token = localStorage.getItem("token");
                    await fetchTripDetails(token, journey.tour_id);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        if (journeyId) {
            loadJourney();
        }
    }, [journeyId]);

    const totalTravelers = useMemo(
        () => participants.adult + participants.child,
        [participants.adult, participants.child]
    );

    if (!journeyId) {
        return <Navigate to="/activities" />;
    }

    const packageTotal = Number(bookingData?.activity_price || 0);

    const vehicleTotal = Number(
        bookingData?.vehicle_total_price || 0
    );
    const grandTotal = packageTotal + vehicleTotal;

    const vehicles = bookingData?.vehicles || [];

    const updateTraveler = (index, field, value) => {
        const updated = [...travelers];
        updated[index] = {
            ...updated[index],
            [field]: value,
        };
        setTravelers(updated);
    };

    const addTraveler = () => {
        if (travelers.length >= totalTravelers) {
            return;
        }

        setTravelers((prev) => [
            ...prev,
            { ...emptyTraveler }
        ]);
    };

    const removeTraveler = (index) => {
        if (travelers.length === 1) return;

        setTravelers((prev) =>
            prev.filter((_, i) => i !== index)
        );
    };

    const validateTravelers = () => {
        if (!travelers || travelers.length === 0) {
            alert("Please add at least one traveler");
            return false;
        }

        const firstTraveler = travelers[0];

        if (
            !firstTraveler?.name?.trim() ||
            !firstTraveler?.email?.trim() ||
            !firstTraveler?.phone?.trim()
        ) {
           alert("Please enter the details for at least one traveler.");
            travelerSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            return false;
        }

        return true;
    };

    const handleProceedToPayment = async () => {
        if (!validateTravelers()) return;

        try {
            await saveBookingTravelers({
                journey_id: journeyId,
                travelers,
            });

            const token = localStorage.getItem("token");

            if (!token) {
                localStorage.setItem("journeyId", journeyId);
                setShowLoginModal(true);
                return;
            }

            navigate(
                `/secure-checkout/${encodeJourneyId(journeyId)}`,
                { replace: true }
            );

        } catch (error) {
            console.error("Failed to save travelers", error);
            alert("Unable to save traveler details");
        }
    };

    return (
        <>
            <main className="min-h-screen text-slate-800">
                <section className="relative h-[30vh] min-h-[220px] sm:min-h-[260px] md:min-h-[320px] lg:h-[20vh] lg:min-h-[300px] overflow-hidden">

                    {/* Background Image */}
                    {bookingData?.tour_image && (
                        <motion.img
                            src={bookingData.tour_image}
                            alt={bookingData?.tour_title || "Tour"}
                            initial={{ scale: 1.15 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 8, ease: "easeOut" }}
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                    )}

                    {/* Dark Overlay */}
                    <div className="absolute inset-0 bg-black/45" />

                    {/* Floating Glow */}
                    <div className="absolute -left-20 top-10 h-48 w-48 sm:h-64 sm:w-64 lg:h-80 lg:w-80 rounded-full bg-cyan-500/20 blur-3xl animate-pulse" />
                    <div className="absolute right-0 bottom-0 h-56 w-56 sm:h-72 sm:w-72 lg:h-96 lg:w-96 rounded-full bg-emerald-400/20 blur-3xl animate-pulse" />

                    {/* Content */}
                    <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-4 sm:px-6 lg:px-10">

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="max-w-3xl"
                        >

                            <span className="inline-flex rounded-full bg-white/10 border border-white/20 backdrop-blur-md px-3 py-1.5 text-[11px] sm:text-xs lg:text-sm text-white">
                                ✈ Premium Travel Experience
                            </span>

                            <h3 className="mt-4 font-bold leading-tight text-white">
                                Booking Summary
                            </h3>

                            <p className="mt-3 leading-6 lg:leading-8 text-slate-200">
                                Review your selected date, guests, vehicles, and traveller details before proceeding to the secure payment gateway.
                            </p>

                        </motion.div>

                    </div>

                </section>

                <section className="mx-auto max-w-7xl px-3 sm:px-5 lg:px-8 py-4 sm:py-6 lg:py-8">
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
                        <div className="space-y-6">
                            <article className="overflow-hidden rounded-2xl bg-white rounded-[26px] border border-[#e5d6bc] soft-shadow">
                                <div className="">
                                    <div className="p-5 sm:p-6">
                                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                            <div>
                                                <h2 className="font-bold leading-tight text-[#434343]">
                                                    {bookingData?.tour_title || "Your selected experience"}
                                                </h2>
                                                <div className="mt-3 flex items-center gap-2 font-medium text-slate-500">
                                                    <MapPin size={17} className="text-teal-600" />
                                                    <span>{bookingData?.location || "Location details pending"}</span>
                                                </div>
                                            </div>

                                        </div>

                                        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div className="rounded-2xl border border-slate-200 bg-[#f5f5f5] p-4">
                                                <div className="flex items-center gap-2 font-semibold text-[#434343]">
                                                    <CalendarDays size={17} className="text-[#434343]" />
                                                    Selected date
                                                </div>
                                                <div className="mt-2 font-bold text-[#434343]">
                                                    {formatDate(bookingData?.selected_date)}
                                                </div>
                                            </div>

                                            <div className="rounded-2xl border border-slate-200 bg-[#f5f5f5] p-4">
                                                <div className="flex items-center gap-2 font-semibold text-[#434343]">
                                                    <Clock size={17} className="text-[#434343]" />
                                                    Selected Time
                                                </div>
                                                <div className="mt-2 flex items-start justify-between gap-4">
                                                    <div className="font-bold text-[#434343]">
                                                        {bookingData?.selected_time || "Time Not Selected"}
                                                    </div>

                                                </div>
                                            </div>
                                        </div>


                                        <div className="mt-5 rounded-2xl border border-slate-200 bg-[#f5f5f5]">
                                            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">


                                                <div className="flex items-center gap-2 font-bold text-[#434343]">
                                                    <Car size={17} className="text-teal-600" />
                                                    Selected package
                                                </div>
                                                <span className="font-bold text-slate-950">
                                                    {bookingData?.package_name || "Package pending"}
                                                </span>
                                            </div>

                                            <div className="divide-y divide-slate-100">
                                                <div
                                                    className="flex items-center justify-between gap-4 px-4 py-3">
                                                    <span className="font-medium text-slate-600">
                                                        {bookingData?.package_name || "Package pending"}
                                                    </span>
                                                    <span className="font-bold text-slate-950">
                                                        ₹{bookingData?.activity_price_per_person} / Person
                                                    </span>
                                                </div>
                                            </div>
                                        </div>







                                        <div className="mt-5 rounded-2xl border border-slate-200 bg-[#f5f5f5]">
                                            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                                                <div className="flex items-center gap-2 font-bold text-[#434343]">
                                                    <Car size={17} className="text-teal-600" />
                                                    Selected vehicles
                                                </div>

                                            </div>

                                            {vehicles.length > 0 ? (
                                                <div className="divide-y divide-slate-100">
                                                    {vehicles.map((vehicle, index) => (
                                                        <div
                                                            key={`${vehicle.vehicle_name}-${index}`}
                                                            className="flex items-center justify-between gap-4 px-4 py-3"
                                                        >
                                                            <span className="font-medium text-slate-600">
                                                                {vehicle.vehicle_name}
                                                            </span>
                                                            <span className="font-bold text-slate-950">
                                                                {formatCurrency(vehicle.vehicle_price)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="px-4 py-4 font-medium text-slate-500">
                                                    No vehicle selected
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>

                            <article className="rounded-[26px] border border-[#e5d6bc] soft-shadow bg-white p-5  sm:p-6">
                                <div className="mb-5 flex items-center gap-3">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#f5f5f5] text-[#434343]">
                                        <Users size={21} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-[#434343]">Participants</h3>
                                        <p className="text-sm text-[#434343]">
                                            Guest count attached to this booking.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                    {[
                                        ["Adult", participants.adult],
                                        ["Child", participants.child],
                                        ["Infant", participants.infant],
                                    ].map(([label, value]) => (
                                        <div
                                            key={label}
                                            className="rounded-2xl border border-slate-200 bg-[#f5f5f5] p-4"
                                        >
                                            <div className="font-semibold text-[#434343]">{label}</div>
                                            <div className="mt-2 font-bold">{value}</div>
                                        </div>
                                    ))}
                                </div>
                            </article>

                            <article className="rounded-2xl rounded-[26px] border border-[#e5d6bc] soft-shadow bg-white p-5 sm:p-6" ref={travelerSectionRef}>
                                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        <h3 className="font-bold text-[#434343]">Traveler Details</h3>
                                        <p className="mt-1 text-[#434343]">
                                            Traveler 1 name, email, and phone are required.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addTraveler}
                                        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#f5f5f5] px-4 text-sm font-bold text-[#434343] shadow-lg shadow-slate-950/15 transition hover:bg-[#f5f5f5]"
                                    >
                                        <Plus size={18} />
                                        Add traveler
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {travelers.map((traveler, index) => (
                                        <div
                                            key={index}
                                            className="rounded-2xl border border-slate-200 bg-[#fbfcfe] p-4 sm:p-5"
                                        >
                                            <div className="mb-4 flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#f5f5f5] text-[#434343] shadow-sm">
                                                        <UserRound size={19} />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-[#434343]">
                                                            Traveler {index + 1}
                                                        </h4>
                                                        <p className="font-medium text-[#434343]">
                                                            {index === 0 ? "Primary contact" : "Additional traveler"}
                                                        </p>
                                                    </div>
                                                </div>

                                                {travelers.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTraveler(index)}
                                                        className="flex h-9 w-9 items-center justify-center rounded-xl border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                                                        aria-label={`Remove traveler ${index + 1}`}
                                                    >
                                                        <Minus size={18} />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {[
                                                    ["name", "Full name", "text", UserRound],
                                                    ["email", "Email address", "email", Mail],
                                                    ["phone", "Phone number", "tel", Phone],
                                                    ["message", "Message", "text", MessageSquare],
                                                ].map(([field, placeholder, type, Icon]) => (
                                                    <label key={field} className="block">
                                                        <span className="mb-2 block font-bold uppercase tracking-wide text-slate-500">
                                                            {placeholder}
                                                        </span>
                                                        <div className="flex h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 transition-within focus-within:border-teal-500 focus-within:ring-4 focus-within:ring-teal-500/10">
                                                            <Icon size={17} className="shrink-0 text-slate-400" />
                                                            <input
                                                                type={type}
                                                                placeholder={placeholder}
                                                                value={traveler[field]}
                                                                required={index === 0 && field !== "message"}
                                                                onChange={(e) =>
                                                                    updateTraveler(index, field, e.target.value)
                                                                }
                                                                className="h-full min-w-0 flex-1 bg-transparent font-medium text-slate-900 outline-none placeholder:text-slate-400"
                                                            />
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </article>
                        </div>


                        <aside className="lg:sticky lg:top-24">
                            <div className="overflow-hidden rounded-[26px] border border-[#e5d6bc] soft-shadow">
                                <div className="border-b border-[#ebe4d7] bg-[#f5f5f5] p-4">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <h3 className="text-[#434343] font-bold">Price Summary</h3>
                                            <p className="mt-1 text-sm text-[#434343]">
                                                Secure checkout breakdown
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5 border-b border-[#ebe4d7] bg-white">
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="font-medium text-[#434343]">Total Guests</span>
                                            <span className="font-bold text-slate-950">
                                                {Number(bookingData?.adults || 0) +
                                                    Number(bookingData?.children || 0) +
                                                    Number(bookingData?.infants || 0)}{" "}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="font-medium text-[#434343]">Package Price</span>
                                            <span className="font-bold text-slate-950">
                                                {formatCurrency(packageTotal)}
                                            </span>
                                        </div>



                                        <div className="flex items-center justify-between gap-4">
                                            <span className="font-medium text-[#434343]">Vehicle Price</span>
                                            <span className="font-bold text-slate-950">
                                                {formatCurrency(vehicleTotal)}
                                            </span>
                                        </div>

                                    </div>
                                    <div className="my-3 h-px bg-slate-200" />

                                    <div className="my-3 rounded-2xl bg-[#f5f5f5] p-4">
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="font-bold text-[#434343]">Total payable</span>
                                        </div>
                                        <div className="pt-2">
                                            <span className="display leading-tight tracking-[-0.014em] text-[#1e1915]">
                                                {formatCurrency(grandTotal)}
                                            </span>
                                        </div>

                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleProceedToPayment}
                                        disabled={loading}
                                        className="mt-[14px] w-full h-[52px] rounded-[15px] bg-[#181512] text-[#f6efe1] font-[600] tracking-[-0.01em] flex items-center justify-center gap-[10px] transition-all hover:bg-[#25211b] disabled:opacity-[.58] cursor-pointer"
                                    >
                                        <ShieldCheck size={18} />
                                        {loading ? "Preparing booking..." : "Proceed to Payment"}
                                    </button>

                                    <div className="mt-4 flex items-start gap-2 rounded-xl bg-[#f5f5f5] p-3 font-medium leading-5 text-[#434343]">
                                        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                                        Your traveler details are saved securely before payment.
                                    </div>
                                </div>
                            </div>
                        </aside>








                        <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t shadow-2xl px-4 py-3 z-50">

                            <div className="flex items-center justify-between">

                                <div>
                                    <p className="text-gray-500">
                                        Total Payable
                                    </p>

                                    <h3 className="font-bold">
                                        {formatCurrency(grandTotal)}
                                    </h3>
                                </div>

                                <button
                                    onClick={handleProceedToPayment}
                                    disabled={loading}
                                    className="bg-black text-white px-6 py-3 rounded-xl font-semibold"
                                >
                                    {loading ? "Loading..." : "Pay Now"}
                                </button>

                            </div>

                        </div>
                    </div>
                </section>
            </main>


            <PaymentLogin
                show={showLoginModal}
                onHide={() => setShowLoginModal(false)}
                journeyId={journeyId}
            />
        </>
    );


}



export default BookingSummary;
